
import React, { useState } from 'react';
import { MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import WelcomeCard from './WelcomeCard';
import BalanceCards from './BalanceCards';
import SavingsChart from './SavingsChart';
import LoanChart from './LoanChart';
import { format } from 'date-fns';

const Account = ({ user, balances, transactions, setActiveTab }) => {
  const [search, setSearch] = useState('');
  const [modalItem, setModalItem] = useState(null);

  // Transform transactions for charts
  const savingsData = transactions
    .filter((tx) => tx.type === 'deposit')
    .reduce((acc, tx) => {
      const month = format(new Date(tx.date), 'MMM yyyy');
      const existing = acc.find((item) => item.month === month);
      if (existing) {
        existing.amount += tx.amount;
      } else {
        acc.push({ month, amount: tx.amount });
      }
      return acc;
    }, [])
    .slice(-12);

  const loanData = transactions
    .filter((tx) => tx.type === 'loan')
    .reduce((acc, tx) => {
      const month = format(new Date(tx.date), 'MMM yyyy');
      const existing = acc.find((item) => item.month === month);
      if (existing) {
        existing.amount += tx.amount;
      } else {
        acc.push({ month, amount: tx.amount });
      }
      return acc;
    }, [])
    .slice(-12);

  const filteredTransactions = transactions.filter(
    (item) =>
      item.party.toLowerCase().includes(search.toLowerCase()) ||
      item.status.toLowerCase().includes(search.toLowerCase()) ||
      item.type.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="p-4 w-full min-h-screen bg-white">
      <WelcomeCard user={user} />
      <BalanceCards balances={balances} />
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
        <SavingsChart data={savingsData} />
        <LoanChart data={loanData} />
      </div>
      <div className="bg-white border border-gray-200 shadow-2xl rounded-xl p-6 mt-6">
        <h3 className="text-lg font-semibold text-gray-700 mb-4">Recent Transactions</h3>
        <div className="mb-4">
          <div className="relative">
            <input
              type="text"
              placeholder="Search by description, type, or status..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brandBlue pl-10"
            />
            <MagnifyingGlassIcon className="h-5 w-5 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm text-gray-700">
            <thead>
              <tr className="bg-gray-100">
                <th className="p-3 font-semibold">Transaction ID</th>
                <th className="p-3 font-semibold">Date</th>
                <th className="p-3 font-semibold">Type</th>
                <th className="p-3 font-semibold">Amount</th>
                <th className="p-3 font-semibold">Description</th>
                <th className="p-3 font-semibold">Status</th>
              </tr>
            </thead>
            <tbody>
              {filteredTransactions.slice(0, 5).map((item) => (
                <tr
                  key={item.id}
                  className="border-b border-gray-200 hover:bg-gray-50 relative"
                  onMouseEnter={() => setModalItem(item)}
                  onMouseLeave={() => setModalItem(null)}
                >
                  <td className="p-3">{item.id}</td>
                  <td className="p-3">{item.date}</td>
                  <td className="p-3 capitalize">{item.type}</td>
                  <td className="p-3">₦{item.amount.toLocaleString()}</td>
                  <td className="p-3">{item.party}</td>
                  <td
                    className={`p-3 ${
                      item.status === 'Completed'
                        ? 'text-green-600'
                        : item.status === 'Pending'
                        ? 'text-yellow-600'
                        : 'text-red-600'
                    }`}
                  >
                    {item.status}
                  </td>
                  {modalItem && modalItem.id === item.id && (
                    <div className="absolute z-10 bg-white border border-gray-200 shadow-lg rounded-lg p-4 w-80 left-1/2 transform -translate-x-1/2 mt-12">
                      <h4 className="text-md font-semibold text-gray-800 mb-2">Transaction Details</h4>
                      <p><strong>Transaction ID:</strong> {item.id}</p>
                      <p><strong>Date:</strong> {item.date}</p>
                      <p><strong>Type:</strong> {item.type}</p>
                      <p><strong>Amount:</strong> ₦{item.amount.toLocaleString()}</p>
                      <p><strong>Description:</strong> {item.party}</p>
                      <p><strong>Status:</strong> {item.status}</p>
                      <p><strong>Notes:</strong> {item.notes}</p>
                    </div>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="mt-4 flex justify-end">
          <button
            onClick={() => setActiveTab('Transaction History')}
            className="px-4 py-2 bg-brandOrange text-white rounded-lg font-semibold hover:bg-orange-600 transition"
          >
            Check More
          </button>
        </div>
      </div>
    </div>
  );
};

export default Account;